<template>
  <div class="users-page">
    <APageHead title="강사·학생 계정 관리" sub="학습자 및 강사 계정을 효율적으로 관리합니다.">
      <template #actions>
        <!-- 재가입한 사용자의 옛 탈퇴 기록은 이메일로만 찾을 수 있어 목록과 별개의 진입점을 둡니다. -->
        <ABtn icon="person_search" @click="showWithdrawalLookup = true">탈퇴 기록 조회</ABtn>
      </template>
    </APageHead>

    <WithdrawalLookupModal v-model="showWithdrawalLookup" />

    <!-- 탭: 계정 목록 / 강사 권한 요청 -->
    <div class="p-tabs users-page__tabs">
      <button
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeTab === 'accounts' }]"
        @click="activeTab = 'accounts'"
      >
        <i class="material-icons-round users-page__tab-icon">group</i>
        계정 목록
      </button>
      <button
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeTab === 'requests' }]"
        @click="activeTab = 'requests'"
      >
        <i class="material-icons-round users-page__tab-icon">how_to_reg</i>
        강사 권한 요청
        <span v-if="pendingRequestCount > 0" class="p-tabs__count">{{ pendingRequestCount }}</span>
      </button>
    </div>

    <!-- 강사 권한 요청 탭 -->
    <InstructorRequestsPanel
      v-if="activeTab === 'requests'"
      @counts-changed="(c) => pendingRequestCount = c.request"
    />

    <!-- 계정 목록 탭 -->
    <template v-if="activeTab === 'accounts'">
    <!--
      통계 카드. 검색 조건과 무관한 전체 기준이며 서버 집계(GET /roles/users/stats,
      docs/회원관리_API.md §3.1-1)를 씁니다. 목록 행(tableData)으로 세면
      현재 페이지 10건만 세게 되므로 여기서는 절대 목록을 세지 않습니다.
      학습자·강사 수는 탈퇴(D)를 뺀 값(Y+N)이고 전체에는 탈퇴도 들어가므로 전체 = 학습자 + 강사 + 탈퇴 입니다.
      운영 배포 전에는 withdrawn_count 가 안 내려오고 학습자·강사 수에 탈퇴가 섞인 종전 값이 오므로,
      탈퇴 표시는 필드가 있을 때만 합니다.
    -->
    <div class="users-page__stats">
      <ACard>
        <AStat label="전체 사용자" :value="formatNum(stats.total_count)" unit="명">
          <span v-if="stats.withdrawn_count !== null" class="users-page__stat-note">탈퇴 {{ formatNum(stats.withdrawn_count) }}명 포함</span>
        </AStat>
      </ACard>
      <ACard><AStat label="학습자" :value="formatNum(stats.learner_count)" unit="명" /></ACard>
      <ACard><AStat label="강사" :value="formatNum(stats.instructor_count)" unit="명" /></ACard>
      <ACard><AStat label="활성 계정" :value="formatNum(stats.active_count)" unit="명" /></ACard>
    </div>

    <ACard overflow="visible">
      <SearchArea :search-fields="searchFields" @search="handleSearch" />
    </ACard>

    <ACard>
      <template #title>
        <span class="users-page__title">{{ $t('navi.03_03') + ' / ' + $t('navi.03_04') }}</span>
      </template>
      <template #sub>학습자·강사 계정 목록 및 상태 관리</template>
      <template #action>
        <APill kind="info">총 {{ formatNum(totalCount) }}명</APill>
      </template>

      <AEmpty v-if="tableData.length === 0" title="등록된 사용자가 없어요" />

      <ATable v-else :columns="columns" :rows="tableData" row-key="idx">
        <template #cell:no="{ index }"><span class="users-page__num">{{ getItemNumber(index) }}</span></template>
        <template #cell:provider="{ row }"><span class="users-page__mono">{{ row.provider_id }}</span></template>
        <template #cell:name="{ row }"><span class="users-page__name">{{ row.name }}</span></template>
        <template #cell:email="{ row }"><span class="users-page__mono">{{ row.email }}</span></template>
        <template #cell:country="{ row }">{{ row.country_name }}</template>
        <template #cell:lang="{ row }">{{ row.first_language }}</template>
        <template #cell:type="{ row }">
          <APill v-if="row.user_type === 'learner'" kind="ok" dot>{{ $t('label.71') }}</APill>
          <APill v-else kind="info" dot>{{ $t('label.72') }}</APill>
        </template>
        <template #cell:status="{ row }">
          <APill v-if="row.active_yn === 'Y'" kind="ok" dot>{{ $t('label.65') }}</APill>
          <APill v-else kind="warn" dot>{{ $t('label.66') }}</APill>
        </template>
        <template #cell:actions="{ row }">
          <ABtn size="sm" icon="visibility" @click="viewDetail(row)">{{ $t('btn.24') }}</ABtn>
        </template>
      </ATable>

      <APagination
        v-if="totalCount > 0"
        :current-page="currentPage"
        :total="totalCount"
        :page-size="itemsPerPage"
        @change="(p) => { currentPage = p; getData(); }"
      />
    </ACard>
    </template>
  </div>
</template>

<script>
import SearchArea from "@/components/SearchArea.vue";
import InstructorRequestsPanel from "@/views/users/components/InstructorRequestsPanel.vue";
import WithdrawalLookupModal from "@/views/users/components/WithdrawalLookupModal.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";

export default {
  name: "UsersIL",
  components: { SearchArea, InstructorRequestsPanel, WithdrawalLookupModal, APageHead, ABtn, ACard, AStat, APill, ATable, AEmpty, APagination },
  data() {
    return {
      activeTab: this.$route.query.tab === "requests" ? "requests" : "accounts",
      pendingRequestCount: 0,
      showWithdrawalLookup: false,
      tableData: [],
      currentPage: 1,
      itemsPerPage: 10,
      totalCount: 0,
      // 상단 통계 카드. 전체 기준 집계라 검색·페이지 이동과 무관하며 마운트 시 한 번만 채웁니다.
      stats: { total_count: 0, learner_count: 0, instructor_count: 0, active_count: 0, withdrawn_count: null },
      searchValues: {},
      searchFields: [
        { key: "name", placeholder: this.$t("label.69"), col: "col-md-2" },
        { key: "email", placeholder: this.$t("label.01"), col: "col-md-2" },
        {
          key: "user_type", placeholder: this.$t("label.70"), col: "col-md-2", type: "select",
          options: [
            { value: "learner", label: this.$t("label.71") },
            { value: "instructor", label: this.$t("label.72") },
          ],
        },
        {
          key: "active_yn", placeholder: this.$t("label.51"), col: "col-md-2", type: "select",
          options: [
            { value: "Y", label: this.$t("label.65") },
            { value: "N", label: this.$t("label.66") },
          ],
        },
      ],
      columns: [
        { key: "no", label: "번호", align: "center", width: 60 },
        { key: "provider", label: this.$t("label.68"), width: 130 },
        { key: "name", label: this.$t("label.69"), width: 130 },
        { key: "email", label: this.$t("label.01") },
        { key: "country", label: this.$t("label.77"), width: 100 },
        { key: "lang", label: this.$t("label.03"), width: 90 },
        { key: "type", label: this.$t("label.70"), width: 100 },
        { key: "status", label: this.$t("label.51"), width: 100 },
        { key: "actions", label: "액션", align: "right", width: 110 },
      ],
    };
  },
  computed: {
    userInfo() { return this.$store.state.auth.user_info || {}; },
    getItemNumber() {
      return (index) => this.totalCount - ((this.currentPage - 1) * this.itemsPerPage + index);
    },
  },
  mounted() {
    if (this.userInfo && this.userInfo.name) {
      this.getData();
      this.getStats();
    }
    this.getPendingRequestCount();
  },
  methods: {
    formatNum(n) { return Number(n || 0).toLocaleString(); },
    // 서버가 count(*) FILTER 로 한 번에 집계해 주므로 사용자가 늘어도 호출 비용은 같습니다.
    async getStats() {
      try {
        const res = await this.$axios({ method: "get", url: "/roles/users/stats", loadingBar: false });
        if (res.status === 200) {
          const d = res.data || {};
          this.stats = {
            total_count: Number(d.total_count) || 0,
            learner_count: Number(d.learner_count) || 0,
            instructor_count: Number(d.instructor_count) || 0,
            active_count: Number(d.active_count) || 0,
            // 운영 미배포면 필드 자체가 없음 → null 로 두고 카드에서 숨깁니다.
            withdrawn_count: d.withdrawn_count === undefined ? null : Number(d.withdrawn_count) || 0,
          };
        }
      } catch (error) {
        console.error("계정 통계 조회 실패:", error);
      }
    },
    async getPendingRequestCount() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/emp/instructor/requests",
          params: { status: "request", limit: 1, offset: 0 },
        });
        this.pendingRequestCount = res.data.total_count || 0;
      } catch (error) {
        console.error("대기중 요청 건수 조회 실패:", error);
      }
    },
    async getData() {
      try {
        const res = await this.$axios({
          method: "get",
          url: "/roles/users",
          params: {
            limit: this.itemsPerPage,
            page: this.currentPage,
            // 탈퇴(D) 계정은 이 목록에서 빼고 헤더 "탈퇴 기록 조회" 모달로만 봅니다. 그래야 "총 N명" 배지가
            // 카드의 학습자·강사 수와 같은 기준이 됩니다. 서버가 스위치를 모르면(운영 미배포) 그냥 무시합니다.
            search_params: JSON.stringify({ ...this.searchValues, exclude_withdrawn: true }),
          },
          loadingBar: true,
        });
        if (res.status === 200) {
          this.tableData = res.data.items;
          this.totalCount = res.data.total_count;
        }
      } catch (error) {
        console.error("Error :", error);
      }
    },
    handleSearch(searchValues) {
      this.searchValues = searchValues;
      this.currentPage = 1;
      this.getData();
    },
    viewDetail(item) {
      this.$router.push({ name: "UserDetail", query: { idx: item.idx } });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.users-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__tabs { flex-wrap: wrap; }
  &__tab-icon { font-size: 15px !important; }
  &__stat-note { font-size: 11px; font-weight: 600; color: $p-ink-soft; }
  &__title { font-size: 13px; font-weight: 800; color: $p-ink; }
  &__num { font-family: $p-font-mono; color: $p-ink-mid; font-weight: 700; }
  &__mono { font-family: $p-font-mono; color: $p-ink2; font-size: 12px; }
  &__name { font-weight: 700; color: $p-ink; }
}
</style>
